import React, { useState, useEffect, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { chartColors } from '../data/dashboardData';
import { dashboardAPI } from '../services/apiClient';
import { appendDateParams, buildDateQueryString } from '../utils/dateParams';

const ProductInsights = ({ dateRange }) => {
  // State for Purchase Categories (real API data)
  const [purchaseCategories, setPurchaseCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // State for Hills Product Types (real API data with pagination and filtering)
  const [hillsProducts, setHillsProducts] = useState([]);
  const [hillsProductsLoading, setHillsProductsLoading] = useState(false);
  const [hillsProductsError, setHillsProductsError] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    species: '',
    lifeStage: ''
  });

  // State for Average Spend (real API data)
  const [avgSpend, setAvgSpend] = useState(null);
  const [avgSpendLoading, setAvgSpendLoading] = useState(false);
  const [avgSpendError, setAvgSpendError] = useState(false);

  // State for Share of Basket (real API data)
  const [shareOfBasket, setShareOfBasket] = useState([]);
  const [shareOfBasketLoading, setShareOfBasketLoading] = useState(false);
  const [shareOfBasketError, setShareOfBasketError] = useState(false);

  // Helper function to build query string with date parameters
  const buildQueryString = useCallback(() => {
    return buildDateQueryString(dateRange);
  }, [dateRange]);

  // Fetch Purchase Categories data
  const fetchPurchaseCategories = useCallback(async () => {
    setLoading(true);
    setError(false);
    
    try {
      const queryString = buildQueryString();
      const response = await dashboardAPI.getProductCategorySummary(queryString);
      
      setPurchaseCategories(response.data?.statistics || response.data || []);
    } catch (error) {
      console.error('Error fetching purchase categories:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch Hills Product Types data
  const fetchHillsProducts = useCallback(async (page = 1, pageSize = 10, search = '', species = '', lifeStage = '') => {
    setHillsProductsLoading(true);
    setHillsProductsError(false);
    
    try {
      const params = new URLSearchParams();
      
      // Pagination
      params.append('Skip', ((page - 1) * pageSize).toString());
      params.append('Take', pageSize.toString());
      
      // Filters
      if (search) params.append('Search', search);
      if (species) params.append('Species', species);
      if (lifeStage) params.append('LifeStage', lifeStage);
      
      // Date range
      appendDateParams(params, dateRange);
      
      const response = await dashboardAPI.getProductPurchaseCounts(params.toString());
      
      const result = response.data;
      setHillsProducts(result?.items || result || []);
      setPagination({
        currentPage: page,
        pageSize: pageSize,
        totalPages: result?.pageCount || 0,
        total: result?.total || 0
      });
    } catch (error) {
      console.error('Error fetching hills products:', error);
      setHillsProductsError(true);
    } finally {
      setHillsProductsLoading(false);
    }
  }, [dateRange]);

  // Fetch Average Spend data
  const fetchAvgSpend = useCallback(async () => {
    setAvgSpendLoading(true);
    setAvgSpendError(false);
    
    try {
      const response = await dashboardAPI.getAvgSpendOnHillsPerSlip(dateRange);
      
      // Handle different possible response structures
      let avgSpendValue = null;
      if (response.data?.averageSpend !== undefined) {
        avgSpendValue = response.data.averageSpend;
      } else if (response.data?.average !== undefined) {
        avgSpendValue = response.data.average;
      } else if (typeof response.data === 'number') {
        avgSpendValue = response.data;
      } else if (response.data?.value !== undefined) {
        avgSpendValue = response.data.value;
      }
      
      setAvgSpend(avgSpendValue);
    } catch (error) {
      console.error('Error fetching average spend:', error);
      setAvgSpendError(true);
    } finally {
      setAvgSpendLoading(false);
    }
  }, [dateRange]);

  // Fetch Share of Basket data
  const fetchShareOfBasket = useCallback(async () => {
    setShareOfBasketLoading(true);
    setShareOfBasketError(false);
    
    try {
      const queryString = buildQueryString();
      const response = await dashboardAPI.getBrandBasketShare(queryString);
      
      setShareOfBasket(response.data?.statistics || response.data || []);
    } catch (error) {
      console.error('Error fetching share of basket:', error);
      setShareOfBasketError(true);
    } finally {
      setShareOfBasketLoading(false);
    }
  }, [dateRange]);

  // Data formatters
  const formatCurrency = (value) => `R${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatPercentage = (value) => `${value.toFixed(1)}%`;
  const formatNumber = (value) => value.toLocaleString();

  // Filter change handlers
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    setFilters(prev => ({ ...prev, search: newSearch }));
    // Debouncing is handled by useEffect below
  };

  const handleSpeciesChange = (e) => {
    const newSpecies = e.target.value;
    setFilters(prev => ({ ...prev, species: newSpecies }));
    fetchHillsProducts(1, pagination.pageSize, filters.search, newSpecies.toUpperCase(), filters.lifeStage.toUpperCase());
  };

  const handleLifeStageChange = (e) => {
    const newLifeStage = e.target.value;
    setFilters(prev => ({ ...prev, lifeStage: newLifeStage }));
    fetchHillsProducts(1, pagination.pageSize, filters.search, filters.species.toUpperCase(), newLifeStage.toUpperCase());
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHillsProducts(1, pagination.pageSize, filters.search, filters.species.toUpperCase(), filters.lifeStage.toUpperCase());
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search, fetchHillsProducts, filters.lifeStage, filters.species, pagination.pageSize]); // Add missing dependencies

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchHillsProducts(newPage, pagination.pageSize, filters.search, filters.species.toUpperCase(), filters.lifeStage.toUpperCase());
    }
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value);
    fetchHillsProducts(1, newPageSize, filters.search, filters.species.toUpperCase(), filters.lifeStage.toUpperCase());
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({ search: '', species: '', lifeStage: '' });
    fetchHillsProducts(1, pagination.pageSize, '', '', '');
  };

  // Fetch data when component mounts or dateRange changes
  useEffect(() => {
    fetchPurchaseCategories();
    fetchAvgSpend();
    fetchShareOfBasket();
    fetchHillsProducts(1, 10, '', '', '');
    // Reset filters and pagination when date range changes
    setFilters({ search: '', species: '', lifeStage: '' });
    // Reset to first page with no filters when date changes
  }, [dateRange, fetchAvgSpend, fetchHillsProducts, fetchPurchaseCategories, fetchShareOfBasket]); // Add missing dependencies

  // Share of Basket Chart Configuration
  const shareBasketChartData = shareOfBasket.length > 0 
    ? shareOfBasket.map(item => item.shareOfBasket || 0)
    : [1]; // Default data to prevent empty series

  const shareBasketChartLabels = shareOfBasket.length > 0 
    ? shareOfBasket.map(item => item.brand || 'Unknown')
    : ['No Data'];

  const shareBasketChartOptions = {
    chart: { 
      type: 'pie', 
      height: 400, 
      background: '#FFFFFF' 
    },
    labels: shareBasketChartLabels,
    colors: [
      chartColors.primary, 
      chartColors.success, 
      chartColors.secondary, 
      chartColors.info, 
      chartColors.light
    ],
    title: { 
      text: 'Share of Basket Insights',
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    legend: {
      position: 'bottom'
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }],
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  return (
    <>
      {/* Products & Purchase Insights */}
      <div className="section-box">
        <div className="section-title">Products & Purchase Insights</div>
        <div className="row">
          {/* Purchase Categories Table */}
          <div className="col s12 m6">
            <h5>Purchase Categories</h5>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="preloader-wrapper active">
                  <div className="spinner-layer spinner-blue-only">
                    <div className="circle-clipper left">
                      <div className="circle"></div>
                    </div>
                    <div className="gap-patch">
                      <div className="circle"></div>
                    </div>
                    <div className="circle-clipper right">
                      <div className="circle"></div>
                    </div>
                  </div>
                </div>
                <p>Loading purchase categories...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#CE060D' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                <p>Error loading purchase categories data</p>
              </div>
            ) : (
              <table className="striped">
                <thead>
                  <tr>
                    <th>Species</th>
                    <th>Life Stage</th>
                    <th>% of Slips</th>
                    <th>Avg Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseCategories.map((category, index) => (
                    <tr key={index}>
                      <td>{category.species}</td>
                      <td>{category.lifeStage}</td>
                      <td>{formatPercentage(category.percentOfSlips)}</td>
                      <td>{formatCurrency(category.avgSpend)}</td>
                    </tr>
                  ))}
                  {purchaseCategories.length === 0 && !loading && !error && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                        No data available for selected date range
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Average Spend Card */}
          <div className="col s12 m6">
            <h5>Average Spend on Hills per Slip</h5>
            <div className="card" style={{ backgroundColor: '#002E6D' }}>
              <div className="card-content">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <i className="fas fa-shopping-cart" style={{ marginRight: '8px', fontSize: '32px', color: 'white' }}></i>
                  <span className="card-title">Avg Spend</span>
                </div>
                {avgSpendLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                    <div className="preloader-wrapper small active">
                      <div className="spinner-layer spinner-white-only">
                        <div className="circle-clipper left">
                          <div className="circle"></div>
                        </div>
                        <div className="gap-patch">
                          <div className="circle"></div>
                        </div>
                        <div className="circle-clipper right">
                          <div className="circle"></div>
                        </div>
                      </div>
                    </div>
                    <span style={{ marginLeft: '10px', color: 'white' }}>Loading...</span>
                  </div>
                ) : avgSpendError ? (
                  <div style={{ color: '#ffcccb', marginTop: '10px' }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '5px' }}></i>
                    Error loading data
                  </div>
                ) : (
                  <h4 style={{ color: 'white' }}>{avgSpend ? formatCurrency(avgSpend) : '-'}</h4>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Hills Product Types Purchased */}
        <div className="row">
          <div className="col s12">
            <h5>Hills Product Types Purchased</h5>
            
            {/* Filter Controls */}
            <div className="row" style={{ marginBottom: '20px' }}>
              <div className="col s12 m3">
                <label style={{ fontSize: '12px', color: '#9e9e9e', marginBottom: '8px', display: 'block' }}>
                  Search Product
                </label>
                <input 
                  type="text" 
                  id="product-search"
                  value={filters.search}
                  onChange={handleSearchChange}
                  placeholder="Search products..."
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px', 
                    height: '40px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div className="col s12 m3">
                <label style={{ fontSize: '12px', color: '#9e9e9e', marginBottom: '8px', display: 'block' }}>
                  Species
                </label>
                <select 
                  value={filters.species} 
                  onChange={handleSpeciesChange}
                  className="browser-default"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px',
                    height: '40px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">All Species</option>
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                </select>
              </div>
              <div className="col s12 m3">
                <label style={{ fontSize: '12px', color: '#9e9e9e', marginBottom: '8px', display: 'block' }}>
                  Life Stage
                </label>
                <select 
                  value={filters.lifeStage} 
                  onChange={handleLifeStageChange}
                  className="browser-default"
                  style={{ 
                    width: '100%', 
                    padding: '8px 12px', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px',
                    height: '40px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">All Life Stages</option>
                  <option value="Puppy">Puppy</option>
                  <option value="Adult">Adult</option>
                  <option value="Mature">Mature</option>
                  <option value="Senior">Senior</option>
                  <option value="Kitten">Kitten</option>
                </select>
              </div>
              <div className="col s12 m3">
                <label style={{ fontSize: '12px', color: '#9e9e9e', marginBottom: '8px', display: 'block', visibility: 'hidden' }}>
                  &nbsp;
                </label>
                <button 
                  className="btn waves-effect waves-light grey lighten-1" 
                  onClick={clearFilters}
                  style={{ 
                    width: '100%', 
                    height: '40px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Table */}
            <div style={{ position: 'relative' }}>
              {/* Loading Overlay */}
              {hillsProductsLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  minHeight: '200px'
                }}>
                  <div className="preloader-wrapper active">
                    <div className="spinner-layer spinner-blue-only">
                      <div className="circle-clipper left">
                        <div className="circle"></div>
                      </div>
                      <div className="gap-patch">
                        <div className="circle"></div>
                      </div>
                      <div className="circle-clipper right">
                        <div className="circle"></div>
                      </div>
                    </div>
                  </div>
                  <p style={{ marginTop: '10px', color: '#666' }}>Loading products...</p>
                </div>
              )}

              {hillsProductsError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                  <p>Error loading product data</p>
                </div>
              ) : (
                <table className="striped">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Species</th>
                      <th>Life Stage</th>
                      <th>Total Purchases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hillsProducts.map((product, index) => (
                      <tr key={index}>
                        <td>{product.product}</td>
                        <td>{product.species}</td>
                        <td>{product.lifeStage}</td>
                        <td>{formatNumber(product.total)}</td>
                      </tr>
                    ))}
                    {hillsProducts.length === 0 && !hillsProductsLoading && !hillsProductsError && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', fontStyle: 'italic', color: '#666' }}>
                          No products found for the selected criteria
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && !hillsProductsLoading && !hillsProductsError && (
              <div style={{ marginTop: '20px' }}>
                {/* Pagination Info */}
                <div style={{ textAlign: 'center', color: '#666', marginBottom: '15px' }}>
                  <p>Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.total)} of {pagination.total} entries</p>
                  <p>Page {pagination.currentPage} of {pagination.totalPages}</p>
                </div>
                
                {/* Pagination Controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  {/* Page Size Selector */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '15px',
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e9ecef',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <label style={{ 
                      fontSize: '15px', 
                      color: '#495057',
                      fontWeight: '600',
                      whiteSpace: 'nowrap'
                    }}>
                      Items per page:
                    </label>
                    
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[5, 10, 25, 50].map(size => (
                        <button
                          key={size}
                          onClick={() => handlePageSizeChange({ target: { value: size } })}
                          style={{
                            padding: '8px 16px',
                            border: pagination.pageSize === size ? '2px solid #007bff' : '2px solid #ced4da',
                            borderRadius: '6px',
                            backgroundColor: pagination.pageSize === size ? '#007bff' : '#ffffff',
                            color: pagination.pageSize === size ? '#ffffff' : '#495057',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            minWidth: '50px'
                          }}
                          onMouseOver={(e) => {
                            if (pagination.pageSize !== size) {
                              e.target.style.backgroundColor = '#e9ecef';
                              e.target.style.borderColor = '#007bff';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (pagination.pageSize !== size) {
                              e.target.style.backgroundColor = '#ffffff';
                              e.target.style.borderColor = '#ced4da';
                            }
                          }}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Page Navigation */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button 
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        backgroundColor: pagination.currentPage === 1 ? '#f5f5f5' : '#fff',
                        color: pagination.currentPage === 1 ? '#999' : '#333',
                        cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Previous
                    </button>
                    
                    {/* Page Numbers */}
                    <div style={{ display: 'flex', gap: '5px' }}>
                      {/* First page */}
                      {pagination.currentPage > 3 && (
                        <>
                          <button 
                            onClick={() => handlePageChange(1)}
                            style={{ 
                              padding: '8px 12px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                              color: '#333',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            1
                          </button>
                          {pagination.currentPage > 4 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                        </>
                      )}
                      
                      {/* Pages around current */}
                      {[...Array(pagination.totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        if (pageNum >= pagination.currentPage - 2 && pageNum <= pagination.currentPage + 2) {
                          return (
                                                         <button 
                               key={pageNum}
                               onClick={() => handlePageChange(pageNum)}
                               style={{ 
                                 padding: '8px 12px', 
                                 border: '1px solid #ddd', 
                                 borderRadius: '4px',
                                 backgroundColor: pageNum === pagination.currentPage ? '#007bff' : '#fff',
                                 color: pageNum === pagination.currentPage ? '#fff' : '#333',
                                 cursor: 'pointer',
                                 fontSize: '14px',
                                 fontWeight: pageNum === pagination.currentPage ? 'bold' : 'normal'
                               }}
                             >
                               {pageNum}
                             </button>
                          );
                        }
                        return null;
                      })}
                      
                      {/* Last page */}
                      {pagination.currentPage < pagination.totalPages - 2 && (
                        <>
                          {pagination.currentPage < pagination.totalPages - 3 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                          <button 
                            onClick={() => handlePageChange(pagination.totalPages)}
                            style={{ 
                              padding: '8px 12px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                              color: '#333',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    
                    <button 
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        backgroundColor: pagination.currentPage === pagination.totalPages ? '#f5f5f5' : '#fff',
                        color: pagination.currentPage === pagination.totalPages ? '#999' : '#333',
                        cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share of Basket Insights */}
      <div className="section-box">
        <div className="section-title">Share of Basket Insights</div>
        
        {shareOfBasketLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="preloader-wrapper active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>
            <p>Loading share of basket data...</p>
          </div>
        ) : shareOfBasketError ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
            <p>Error loading share of basket data</p>
          </div>
        ) : shareOfBasket.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
            <p>No share of basket data available for selected date range</p>
          </div>
        ) : (
          <>
            <div className="row">
              <div className="col s12">
                <div className="chart-container">
                  {shareOfBasketLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
                      <p>Loading share of basket data...</p>
                    </div>
                  ) : shareOfBasketError ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#d32f2f' }}>
                      <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
                      <p>Error loading share of basket data</p>
                    </div>
                  ) : (
                    <Chart
                      options={shareBasketChartOptions}
                      series={shareBasketChartData}
                      type="pie"
                      height={400}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col s12">
                <table className="striped">
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>Total Spend</th>
                      <th>Share (%)</th>
                      <th>Product Count</th>
                      <th>Avg per Basket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shareOfBasket.map((item, index) => (
                      <tr key={index}>
                        <td>{item.brand}</td>
                        <td>{formatCurrency(item.totalCost)}</td>
                        <td>{formatPercentage(item.shareOfBasket)}</td>
                        <td>{formatNumber(item.productCount)}</td>
                        <td>{formatCurrency(item.avgPerBasket)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ProductInsights; 